/**
 * Unified data fetching hook for macro indicators
 * Combines IMF and World Bank APIs with proper error handling
 *
 * DATA INTEGRITY POLICY:
 * - Never fall back to mock/fake data
 * - Show "unavailable" status when data cannot be fetched
 * - All displayed data must come from verified sources
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { dataService } from '../services';
import type { DataPoint, IndicatorMetadata, IndicatorCategory } from '../types/indicator';
import { getIndicatorById, getIndicatorsByCategory, searchIndicators } from '../services/data/indicatorRegistry';

export type DataSourceType = 'imf' | 'worldbank' | 'unavailable';

export interface IndicatorDataResult {
  data: DataPoint[];
  forecast: DataPoint[] | null;
  statistics: {
    min: number;
    max: number;
    avg: number;
    latest: number | null;
    latestYear: number | null;
  } | null;
  yoyChange: { year: number; change: number }[];
  isLoading: boolean;
  error: Error | null;
  metadata: IndicatorMetadata | null;
  source: DataSourceType;
  lastUpdated: Date | null;
  refetch: () => void;
}

export interface PeerComparisonResult {
  data: Record<string, DataPoint[]>;
  latestValues: Record<string, { year: number; value: number } | null>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// Calculate statistics from data points
const calculateStatistics = (data: DataPoint[]): IndicatorDataResult['statistics'] => {
  const validData = data.filter(d => d.value !== null);
  if (validData.length === 0) return null;

  const values = validData.map(d => d.value as number);
  const sorted = [...validData].sort((a, b) => b.year - a.year);
  const latest = sorted[0];

  return {
    min: Math.min(...values),
    max: Math.max(...values),
    avg: values.reduce((a, b) => a + b, 0) / values.length,
    latest: latest?.value ?? null,
    latestYear: latest?.year ?? null,
  };
};

// Calculate year-over-year changes
const calculateYoYChange = (data: DataPoint[]): { year: number; change: number }[] => {
  const sorted = [...data].sort((a, b) => a.year - b.year);
  const changes: { year: number; change: number }[] = [];

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1].value;
    const curr = sorted[i].value;
    if (prev !== null && curr !== null && prev !== 0) {
      changes.push({
        year: sorted[i].year,
        change: ((curr - prev) / Math.abs(prev)) * 100,
      });
    }
  }

  return changes;
};

// Global state for tracking API health
let apisHealthy = true;

// Main hook for fetching indicator data
export function useIndicatorData(
  indicatorId: string,
  countryCode: string = 'UZB',
  years: number = 15
): IndicatorDataResult {
  const [data, setData] = useState<DataPoint[]>([]);
  const [forecast, setForecast] = useState<DataPoint[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [source, setSource] = useState<DataSourceType>('unavailable');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const metadata = useMemo(() => getIndicatorById(indicatorId) ?? null, [indicatorId]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch from the unified data service (IMF/World Bank)
      const timeSeries = await dataService.getMacroTimeSeries(countryCode, indicatorId, years);

      if (timeSeries && timeSeries.data.length > 0) {
        setData(timeSeries.data);
        // Determine source from data points or default to worldbank
        const firstPoint = timeSeries.data.find(d => d.source);
        setSource(firstPoint?.source === 'imf' ? 'imf' : 'worldbank');

        // Try to get forecasts
        try {
          const forecastData = await dataService.getForecasts(countryCode, indicatorId);
          if (forecastData) {
            setForecast(forecastData);
          }
        } catch {
          setForecast(null);
        }
        setLastUpdated(new Date());
      } else {
        // No data available - do NOT fall back to mock data
        setData([]);
        setSource('unavailable');
        setLastUpdated(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to fetch indicator data'));
      // Do NOT use mock data on error - show unavailable status
      setData([]);
      setSource('unavailable');
    } finally {
      setIsLoading(false);
    }
  }, [indicatorId, countryCode, years]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const statistics = useMemo(() => calculateStatistics(data), [data]);
  const yoyChange = useMemo(() => calculateYoYChange(data), [data]);

  return {
    data,
    forecast,
    statistics,
    yoyChange,
    isLoading,
    error,
    metadata,
    source,
    lastUpdated,
    refetch: fetchData,
  };
}

// Hook for fetching multiple indicators at once
export function useMultipleIndicators(
  indicatorIds: string[],
  countryCode: string = 'UZB'
): {
  data: Record<string, DataPoint[]>;
  sources: Record<string, DataSourceType>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const [data, setData] = useState<Record<string, DataPoint[]>>({});
  const [sources, setSources] = useState<Record<string, DataSourceType>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const results: Record<string, DataPoint[]> = {};
      const sourceResults: Record<string, DataSourceType> = {};

      await Promise.all(
        indicatorIds.map(async (id) => {
          try {
            const timeSeries = await dataService.getMacroTimeSeries(countryCode, id, 10);
            if (timeSeries?.data && timeSeries.data.length > 0) {
              results[id] = timeSeries.data;
              const firstPoint = timeSeries.data.find(d => d.source);
              sourceResults[id] = firstPoint?.source === 'imf' ? 'imf' : 'worldbank';
            } else {
              results[id] = [];
              sourceResults[id] = 'unavailable';
            }
          } catch {
            results[id] = [];
            sourceResults[id] = 'unavailable';
          }
        })
      );

      setData(results);
      setSources(sourceResults);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to fetch indicators'));
      // Set all as unavailable - do NOT use mock data
      const emptyResults: Record<string, DataPoint[]> = {};
      const unavailableSources: Record<string, DataSourceType> = {};
      indicatorIds.forEach(id => {
        emptyResults[id] = [];
        unavailableSources[id] = 'unavailable';
      });
      setData(emptyResults);
      setSources(unavailableSources);
    } finally {
      setIsLoading(false);
    }
  }, [indicatorIds, countryCode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, sources, isLoading, error, refetch: fetchData };
}

// Hook for peer comparison - Central Asia & Silk Road Corridor
export function usePeerComparison(
  indicatorId: string,
  countryCodes: string[] = ['UZB', 'KAZ', 'KGZ', 'TJK', 'TKM', 'AZE', 'GEO', 'MNG']
): PeerComparisonResult {
  const [data, setData] = useState<Record<string, DataPoint[]>>({});
  const [latestValues, setLatestValues] = useState<Record<string, { year: number; value: number } | null>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const comparison = await dataService.getPeerComparison(countryCodes, [indicatorId]);

      const results: Record<string, DataPoint[]> = {};
      const latest: Record<string, { year: number; value: number } | null> = {};

      countryCodes.forEach(code => {
        const value = comparison[indicatorId]?.values[code];
        const year = comparison[indicatorId]?.year || new Date().getFullYear() - 1;

        if (value !== null && value !== undefined) {
          results[code] = [{ year, value }];
          latest[code] = { year, value };
        } else {
          results[code] = [];
          latest[code] = null;
        }
      });

      setData(results);
      setLatestValues(latest);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to fetch peer comparison'));
      // Do NOT use mock data - set empty results
      const emptyResults: Record<string, DataPoint[]> = {};
      const emptyLatest: Record<string, { year: number; value: number } | null> = {};
      countryCodes.forEach(code => {
        emptyResults[code] = [];
        emptyLatest[code] = null;
      });
      setData(emptyResults);
      setLatestValues(emptyLatest);
    } finally {
      setIsLoading(false);
    }
  }, [indicatorId, countryCodes]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, latestValues, isLoading, error, refetch: fetchData };
}

// Hook for searching indicators
export function useIndicatorSearch(query: string): {
  results: IndicatorMetadata[];
  isSearching: boolean;
} {
  const [results, setResults] = useState<IndicatorMetadata[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    setIsSearching(true);

    // Debounce search
    const timer = setTimeout(() => {
      const searchResults = searchIndicators(query);
      setResults(searchResults.slice(0, 20)); // Limit to 20 results
      setIsSearching(false);
    }, 150);

    return () => clearTimeout(timer);
  }, [query]);

  return { results, isSearching };
}

// Hook for getting indicators by category
export function useCategoryIndicators(category: IndicatorCategory): {
  indicators: IndicatorMetadata[];
  primaryIndicators: IndicatorMetadata[];
} {
  const indicators = useMemo(() => getIndicatorsByCategory(category), [category]);
  const primaryIndicators = useMemo(
    () => indicators.filter(ind => ind.isPrimary),
    [indicators]
  );

  return { indicators, primaryIndicators };
}

// Hook for checking API health
export function useAPIHealth(): {
  imfHealthy: boolean;
  wbHealthy: boolean;
  checkHealth: () => Promise<void>;
} {
  const [imfHealthy, setImfHealthy] = useState(true);
  const [wbHealthy, setWbHealthy] = useState(true);

  const checkHealth = useCallback(async () => {
    try {
      const health = await dataService.checkDataSourcesHealth();
      setImfHealthy(health.imf);
      setWbHealthy(health.worldBank);
      apisHealthy = health.imf || health.worldBank;
    } catch {
      setImfHealthy(false);
      setWbHealthy(false);
      apisHealthy = false;
    }
  }, []);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  return { imfHealthy, wbHealthy, checkHealth };
}

export default useIndicatorData;
